import { useEffect, useState } from 'react'
import { registerPushNotifications } from './pushNotifications'
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { supabase, signInWithMemberId, getMyProfile, registreerAppOpening } from './lib/supabase'
import LoginPage from './pages/LoginPage'
import MemberApp from './pages/MemberApp'
import AdminPage from './pages/AdminPage'
import ResetWachtwoord from './pages/ResetWachtwoord'
import './index.css'

// Vangt Universal Links op (bv. vanuit de wachtwoord-reset-mail — app.vitaal-sterk.nl/
// reset-wachtwoord staat geregistreerd in apple-app-site-association) en navigeert
// er BINNEN de al geladen app naartoe, i.p.v. de native WebView een hele nieuwe
// paginalading te laten doen via window.location.href. Dat laatste gaf bij een
// koude start (de link opent de app zelf pas) soms een wit scherm: de WebView
// kreeg dan een navigatie-opdracht te verwerken nog vóórdat de app volledig was
// opgestart. Client-side routeren binnen de al draaiende React-app is robuuster.
function DeepLinkHandler() {
  const navigate = useNavigate()

  useEffect(() => {
    function handleOpenUrl(url) {
      try {
        const parsed = new URL(url)
        const hashParams = new URLSearchParams(parsed.hash.replace(/^#/, ''))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        // Oude-stijl links met tokens in de hash: de sessie handmatig zetten,
        // want een client-side route-wissel triggert Supabase's automatische
        // hash-detectie niet (die draait alleen bij een echte paginalading).
        if (accessToken && refreshToken) {
          supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
            .catch(e => console.error('Kon sessie uit universal link niet zetten:', e))
        }
        navigate(parsed.pathname + parsed.search + parsed.hash, { replace: true })
      } catch (e) {
        console.error('Kon universal link niet verwerken:', e)
      }
    }

    let urlListener
    if (Capacitor.isNativePlatform()) {
      // Warme start: de app stond al open of op de achtergrond.
      CapacitorApp.addListener('appUrlOpen', ({ url }) => handleOpenUrl(url))
        .then(l => { urlListener = l })
        .catch(e => console.error('appUrlOpen listener fout:', e))

      // Koude start: de link heeft de app zelf net geopend. Dan kan appUrlOpen
      // al afvuren vóórdat bovenstaande listener geregistreerd is, waardoor de
      // link stilletjes verloren gaat en de app gewoon op het beginscherm opent.
      // getLaunchUrl() vangt dat geval alsnog op.
      CapacitorApp.getLaunchUrl()
        .then(result => {
          if (result?.url) handleOpenUrl(result.url)
        })
        .catch(e => console.error('getLaunchUrl fout:', e))
    }

    return () => { urlListener?.remove() }
  }, [navigate])

  return null
}

export default function App() {
  const [session, setSession] = useState(undefined)
  // undefined = profiel nog niet geladen (spinner tonen, NIET het inlogscherm —
  // anders flitst bij elke app-start/token-refresh even het inlogscherm op),
  // null = bevestigd geen profiel bij deze sessie, object = ingelogde lid-data.
  const [member, setMember] = useState(undefined)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data?.session ?? null)
      if (data?.session) loadProfile(data.session.user.id)
      else setMember(null)
    }).catch(() => { setSession(null); setMember(null) })

    let subscription
    try {
      const { data: listener } = supabase.auth.onAuthStateChange((_e, s) => {
        setSession(s)
        if (s) loadProfile(s.user.id)
        else setMember(null)
      })
      subscription = listener.subscription
    } catch (e) {
      console.error('Auth listener error:', e)
      setSession(null)
      setMember(null)
    }

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  // Haalt het lid-profiel op bij de huidige sessie. Belangrijk: dit wordt niet
  // alleen bij het opstarten aangeroepen, maar ook bij elke automatische
  // token-refresh (standaard elk uur). Een tijdelijke netwerkhapering hier
  // mag de gebruiker NOOIT terug naar het inlogscherm sturen — de sessie zelf
  // is dan nog gewoon geldig, alleen dit ene verzoek faalde. Vandaar: bij een
  // fout proberen we het na een korte pauze nog een keer, en zetten we het lid
  // alleen op 'niet gevonden' als de server écht zegt dat er geen profiel bij
  // dit account hoort (PGRST116 — geen rijen).
  async function loadProfile(userId, poging = 1) {
    try {
      const profile = await getMyProfile(userId)
      setMember(profile)
      // Eigen try/catch: als dit faalt mag dat nooit de profiel-retry hieronder
      // triggeren (member is dan al succesvol gezet).
      try { registerPushNotifications(profile.id) } catch (e) { console.error('Push-registratie mislukt:', e) }
      registreerAppOpening(Capacitor.getPlatform()).catch(e => console.error('App-opening registreren mislukt:', e))
    } catch (e) {
      if (e?.code === 'PGRST116') {
        // Echt geen lid gekoppeld aan dit account — dan terecht naar het inlogscherm.
        setMember(null)
        return
      }
      if (poging < 3) {
        // Vermoedelijk een tijdelijke netwerkfout: nog even opnieuw proberen
        // i.p.v. de gebruiker meteen uit te loggen.
        setTimeout(() => loadProfile(userId, poging + 1), 1500 * poging)
        return
      }
      console.error('Profiel laden mislukt na meerdere pogingen:', e)
      // Sessie blijft geldig; laat het scherm op de spinner staan zodat de
      // gebruiker niet onterecht wordt teruggestuurd naar het inlogscherm.
      // Bij de volgende automatische token-refresh (of app-herstart) wordt
      // het opnieuw geprobeerd.
    }
  }

  async function handleLogin(memberId, pin) {
    await signInWithMemberId(memberId, pin)
  }

  return (
    <BrowserRouter>
      <DeepLinkHandler />
      {session === undefined || (session && member === undefined) ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100svh', background: '#f5f7f4' }}>
          <div className="spinner" style={{ margin: 0 }} />
        </div>
      ) : (
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/reset-wachtwoord" element={<ResetWachtwoord />} />
          <Route
            path="/*"
            element={
              session && member
                ? <MemberApp member={member} onSignOut={() => { setSession(null); setMember(null) }} />
                : <LoginPage onLogin={handleLogin} />
            }
          />
        </Routes>
      )}
    </BrowserRouter>
  )
}
